/**
 * THE ARROWS NO FONT WE SHIP WILL DRAW, DRAWN ONCE AND USED BY BOTH SURFACES.
 *
 * The palette button draws them as JSX and the editor's node view draws them as DOM, so the
 * PATHS live here and each side renders them its own way. Forking the path data would let the
 * button and the thing it inserts drift apart, which is exactly the bug this file prevents: a
 * teacher pressed an arrow whose harpoons lean one way and got a glyph that leans neither.
 *
 * ── WHY ⇄ AND ⇌ ARE HERE TOO, THOUGH UNICODE HAS THEM ───────────────────────────────────────
 * Having a codepoint is not having a glyph. Measured across the field's whole stack — KaTeX_Main,
 * KaTeX_AMS, Georgia, Times — not one face contains U+21C4 or U+21CC, and KaTeX itself never
 * needs them because it BUILDS an equilibrium by stacking two harpoons. So every ⇄ and ⇌ in the
 * reaction field was being drawn by whatever last-resort font the reader's machine happened to
 * own: Apple Symbols here, Segoe UI Symbol on Windows, Noto on Android, each a different weight
 * and size, none of them matching the text beside it and none of them ours to scale. That is why
 * the inserted arrow looked smaller than its own button no matter what the button did.
 *
 * Drawn, they are the same arrow at the same size on every machine.
 */

/**
 * Each arrow as its strokes, in a box 14 units tall. Only the WIDTH differs between them.
 *
 * One box height and one stroke width for the whole family, and every arrow rendered at the
 * same units-per-pixel — see `EM_PER_UNIT` — is the whole of why they match. A long arrow
 * squeezed into the short one's rendered width would draw the same 1.3 units thinner.
 */
export const ARROWS: Record<DrawnArrow, { width: number; paths: readonly string[] }> = {
  /* The plain arrows. Drawn for the same reason as the rest: not one font the field falls back
     to carries U+2192, U+2190 or U+2194 either, so their size and weight were the reader's
     machine's to decide and they never matched the arrows beside them. */
  right: { width: 24, paths: ['M3 7 H21 M21 7 L18.4 4.7 M21 7 L18.4 9.3'] },
  left: { width: 24, paths: ['M3 7 H21 M3 7 L5.6 4.7 M3 7 L5.6 9.3'] },
  bothHeads: { width: 24, paths: ['M3 7 H21 M21 7 L18.4 4.7 M21 7 L18.4 9.3 M3 7 L5.6 4.7 M3 7 L5.6 9.3'] },
  /**
   * THE SHAFT A LABEL SITS ON.
   *
   * Was `\u27f6`, a real character out of KaTeX_Main and so not a fallback risk — but measured
   * beside the drawn arrows it is a different arrow: 1px of stroke against their 1.89px, and
   * 35.3px of ink against 26.1px. Half the weight, a third longer. A reaction holding both read
   * as two different notations, which is what got reported.
   *
   * 27 units, so it still comes out about as long as the character it replaces and a label
   * still has something to sit over.
   */
  longRight: { width: 27, paths: ['M3 7 H24 M24 7 L21.4 4.7 M24 7 L21.4 9.3'] },
  /** An equilibrium whose harpoons are deliberately unequal: the reaction mostly sits one way. */
  eqForward: { width: 24, paths: ['M3 5.5 H21 M21 5.5 L18.8 3.7', 'M8 9.5 H16 M8 9.5 L10.2 11.3'] },
  eqBackward: { width: 24, paths: ['M8 5.5 H16 M16 5.5 L13.8 3.7', 'M3 9.5 H21 M3 9.5 L5.2 11.3'] },
  /** A balanced equilibrium: the same two harpoons, both the full width. */
  equilibrium: { width: 24, paths: ['M3 5.5 H21 M21 5.5 L18.8 3.7', 'M3 9.5 H21 M3 9.5 L5.2 11.3'] },
  /* A reaction that runs both ways is two ARROWS, not two harpoons: full heads, so it cannot be
     misread as the equilibrium directly above it in the palette. */
  bothWays: { width: 24, paths: [
    'M3 5.5 H21 M21 5.5 L18.8 3.7 M21 5.5 L18.8 7.3',
    'M3 9.5 H21 M3 9.5 L5.2 7.7 M3 9.5 L5.2 11.3',
  ] },
  /**
   * RETROSYNTHETIC: the double shaft that says "is made from", read right to left.
   *
   * Two rails rather than a heavier single one, which is what a double arrow IS — and what
   * keeps it apart from the equilibrium's two harpoons, whose heads are half-heads on opposite
   * ends. The head is one solid V spanning both rails.
   */
  doubleRight: { width: 24, paths: [
    'M3 5.6 H19.6', 'M3 8.4 H19.6', 'M21 7 L18.1 4.3 M21 7 L18.1 9.7',
  ] },
  /**
   * DASHED: a step that is proposed rather than performed — a theoretical or hypothetical one.
   *
   * The dashes are drawn as separate strokes rather than with `stroke-dasharray`, so they land
   * in the same place at every size. `arrowMarkup` sets one stroke width for the family and a
   * dash array would be scaled by it.
   */
  dashedRight: { width: 24, paths: [
    'M3 7 H6.6', 'M9.4 7 H13', 'M15.8 7 H19.4', 'M21 7 L18.4 4.7 M21 7 L18.4 9.3',
  ] },
};

export type DrawnArrow =
  | 'right' | 'left' | 'bothHeads' | 'longRight'
  | 'eqForward' | 'eqBackward' | 'equilibrium' | 'bothWays'
  | 'doubleRight' | 'dashedRight';

/** Every arrow is drawn in a box this many units tall. */
const BOX_HEIGHT = 14;

/**
 * How much of an em one drawing unit is worth.
 *
 * The one number that holds the family together. A 1.3-unit stroke is the same thickness on
 * screen in every arrow only while they all render at this scale, so a long arrow is given a
 * proportionally longer BOX rather than being squeezed into the short one's.
 *
 * Its value is the short arrow's old hand-set size, 1.45em across 24 units, so nothing that
 * already looked right moved when the long arrow joined them.
 */
const EM_PER_UNIT = 1.45 / 24;

/** The width this arrow wants, in em, at the font size it is drawn in. */
export const arrowEm = (name: DrawnArrow): number => ARROWS[name].width * EM_PER_UNIT;

/** The height every arrow wants, in em. One value, because every box is the same height. */
export const ARROW_EM_HEIGHT = BOX_HEIGHT * EM_PER_UNIT;

/** The same arrow as markup, for the node view, which builds DOM rather than JSX. */
export function arrowMarkup(name: DrawnArrow): string {
  const { width, paths } = ARROWS[name];
  const d = paths.map(p => `<path d="${p}"/>`).join('');
  return `<svg aria-hidden="true" viewBox="0 0 ${width} ${BOX_HEIGHT}" class="r-arrow-svg"`
    + ` style="width:${arrowEm(name).toFixed(3)}em;height:${ARROW_EM_HEIGHT.toFixed(3)}em"`
    + ` fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round">${d}</svg>`;
}
